import React from 'react';
import {
  Target,
  ChartBar,
  FileText,
  ArrowRight,
  CircleDollarSign,
  Users,
  Building2,
  GitBranch,
  Globe,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Workflow,
  CreditCard,
  Fingerprint,
  Database,
  Network
} from 'lucide-react';

// Réutilisation des composants de base (Section, AnalysisPoint, Metric)
const Section = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`mb-8 bg-white rounded-xl border border-gray-200 ${className}`}>
    <div className="p-6 border-b border-gray-100">
      <div className="flex items-center">
        <Icon className="w-6 h-6 text-blue-600 mr-3" />
        <h2 className="text-xl font-semibold">{title}</h2>
      </div>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const AnalysisPoint = ({ title, status = "info", children }) => {
  const statusColors = {
    success: "border-green-500 bg-green-50",
    warning: "border-yellow-500 bg-yellow-50",
    error: "border-red-500 bg-red-50",
    info: "border-blue-500 bg-blue-50"
  };

  return (
    <div className={`border-l-4 rounded-r-lg p-4 ${statusColors[status]}`}>
      <h3 className="font-medium mb-2">{title}</h3>
      <div className="text-sm space-y-2">{children}</div>
    </div>
  );
};

const Metric = ({ label, value, trend }) => (
  <div className="bg-gray-50 p-4 rounded-lg">
    <div className="text-sm text-gray-600 mb-1">{label}</div>
    <div className="text-2xl font-semibold">{value}</div>
    {trend && (
      <div className="text-sm text-gray-500 mt-1">{trend}</div>
    )}
  </div>
);

const DigitalMaliAnalysis = () => {
  // Contexte et objectifs
  const projectContext = {
    vision: "Transformation digitale des services financiers au Mali",
    objectives: [
      "Développement infrastructure digitale nationale",
      "Inclusion financière digitale",
      "Modernisation des services bancaires",
      "Promotion de l'innovation fintech"
    ],
    scope: {
      institutions: [
        "13 banques commerciales",
        "Institutions de microfinance",
        "Opérateurs télécom",
        "Fintechs"
      ],
      composants: [
        {
          name: "eKYC National",
          description: "Plateforme centralisée de vérification d'identité",
          status: "En développement"
        },
        {
          name: "Infrastructure Paiements",
          description: "Rails de paiement digitaux nationaux",
          status: "Phase pilote"
        },
        {
          name: "Crédit Digital",
          description: "Plateforme d'évaluation et octroi crédit",
          status: "Conception"
        }
      ]
    }
  };

  // Analyse technique
  const technicalAnalysis = {
    infrastructure: [
      {
        component: "Plateforme eKYC",
        features: [
          "Vérification biométrique",
          "API REST standardisées",
          "Intégration RAVEC",
          "Audit trail complet"
        ],
        technologies: [
          "Biométrie ABIS",
          "Blockchain pour audit",
          "APIs sécurisées",
          "Cloud souverain"
        ],
        status: "60% complété"
      },
      {
        component: "Infrastructure Paiements",
        features: [
          "Paiements instantanés",
          "QR Code national",
          "Compensation multilatérale",
          "Interopérabilité mobile money"
        ],
        technologies: [
          "ISO 20022",
          "APIs temps réel",
          "Kubernetes",
          "Event streaming"
        ],
        status: "Phase pilote"
      }
    ],
    challenges: [
      {
        category: "Technique",
        items: [
          "Connectivité réseau intermittente",
          "Infrastructure cloud locale limitée",
          "Intégration systèmes legacy",
          "Sécurité données sensibles"
        ]
      },
      {
        category: "Opérationnel",
        items: [
          "Formation personnel technique",
          "Support 24/7",
          "Gestion incidents",
          "Maintenance systèmes"
        ]
      }
    ]
  };

  // Impacts attendus
  const impacts = {
    financier: [
      {
        metric: "Coûts transactions",
        target: "-60%",
        timeline: "2025"
      },
      {
        metric: "Inclusion financière",
        target: "+35%",
        timeline: "2025"
      },
      {
        metric: "Volume transactions digital",
        target: "x5",
        timeline: "2025"
      }
    ],
    social: [
      {
        metric: "Accès services financiers",
        target: "80% population",
        timeline: "2025"
      },
      {
        metric: "Emplois tech créés",
        target: "5000+",
        timeline: "2025"
      }
    ]
  };

  // Plan d'exécution
  const implementation = {
    phases: [
      {
        name: "Phase 1 - Infrastructure Base",
        period: "2023-2024",
        components: [
          "eKYC plateforme base",
          "Rails paiement core",
          "APIs standards"
        ],
        status: "En cours - 60%"
      },
      {
        name: "Phase 2 - Services Avancés",
        period: "2024-2025",
        components: [
          "Crédit scoring",
          "Open banking",
          "Analytics avancés"
        ],
        status: "Planifié"
      }
    ],
    risques: [
      {
        risque: "Adoption institutions",
        impact: "Élevé",
        mitigation: "Programme accompagnement dédié"
      },
      {
        risque: "Infrastructure tech",
        impact: "Moyen",
        mitigation: "Partenariats techniques internationaux"
      },
      {
        risque: "Réglementation",
        impact: "Élevé",
        mitigation: "Groupe travail régulateur"
      }
    ],
    governance: {
      comités: [
        "Comité pilotage national",
        "Comité technique",
        "Groupe travail banques",
        "Taskforce réglementaire"
      ],
      fréquence: "Suivi mensuel",
      reporting: "Dashboard temps réel"
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">
            Analyse Détaillée - Digital Mali 2025
          </h1>
          <div className="flex space-x-4">
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Phase: Implémentation
            </span>
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
              Progression: 60%
            </span>
          </div>
        </div>

        {/* Vision et Contexte */}
        <Section title="Vision et Contexte" icon={Target}>
          <div className="space-y-6">
            <AnalysisPoint title="Vision Stratégique">
              <p>{projectContext.vision}</p>
              <div className="mt-4">
                <h4 className="font-medium mb-2">Objectifs Clés:</h4>
                <ul className="list-disc pl-4 space-y-1">
                  {projectContext.objectives.map((obj, idx) => (
                    <li key={idx}>{obj}</li>
                  ))}
                </ul>
              </div>
            </AnalysisPoint>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Institutions */}
              <div>
                <h4 className="font-medium mb-3">Institutions Participantes</h4>
                <ul className="space-y-2">
                  {projectContext.scope.institutions.map((inst, idx) => (
                    <li key={idx} className="flex items-center">
                      <Building2 className="w-4 h-4 mr-2 text-blue-500" />
                      {inst}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Composants */}
              <div>
                <h4 className="font-medium mb-3">Composants Clés</h4>
                {projectContext.scope.composants.map((comp, idx) => (
                  <div key={idx} className="mb-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <span className="font-medium">{comp.name}</span>
                      <span className="text-sm px-2 py-1 bg-blue-100 text-blue-700 rounded">
                        {comp.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{comp.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Analyse Technique */}
        <Section title="Infrastructure & Architecture" icon={GitBranch}>
          <div className="space-y-6">
            {/* Composants techniques */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {technicalAnalysis.infrastructure.map((infra, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-medium">{infra.component}</h3>
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                      {infra.status}
                    </span>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-medium mb-2">Fonctionnalités:</h4>
                      <ul className="space-y-1">
                        {infra.features.map((feature, i) => (
                          <li key={i} className="flex items-center text-sm">
                            <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium mb-2">Technologies:</h4>
                      <ul className="space-y-1">
                        {infra.technologies.map((tech, i) => (
                          <li key={i} className="flex items-center text-sm">
                            <Database className="w-4 h-4 mr-2 text-blue-500" />
                            {tech}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Défis techniques */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {technicalAnalysis.challenges.map((challenge, idx) => (
                <AnalysisPoint 
                  key={idx} 
                  title={`Défis ${challenge.category}`}
                  status="warning"
                >
                  <ul className="space-y-1">
                    {challenge.items.map((item, i) => (
                      <li key={i} className="flex items-center">
                        <AlertTriangle className="w-4 h-4 mr-2 text-yellow-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </AnalysisPoint>
              ))}
            </div>
          </div>
        </Section>

        {/* Impacts */}
        <Section title="Impacts Attendus" icon={ChartBar}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium mb-4">Impact Financier</h3>
              <div className="space-y-3">
                {impacts.financier.map((impact, idx) => (
                  <Metric
                    key={idx}
                    label={impact.metric}
                    value={impact.target}
                    trend={`Objectif ${impact.timeline}`}
                  />
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-medium mb-4">Impact Social</h3>
              <div className="space-y-3">
                {impacts.social.map((impact, idx) => (
                  <Metric
                    key={idx}
                    label={impact.metric}
                    value={impact.target}
                    trend={`Objectif ${impact.timeline}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Plan d'exécution */}
        <Section title="Plan d'Exécution" icon={Workflow}>
          <div className="space-y-6">
            {/* Phases */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {implementation.phases.map((phase, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-medium">{phase.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${
                      phase.status.includes('En cours') 
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {phase.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-3">Période: {phase.period}</p>
                    <h4 className="text-sm font-medium mb-2">Composants:</h4>
                    <ul className="space-y-1">
                      {phase.components.map((component, i) => (
                        <li key={i} className="flex items-center text-sm">
                          <ArrowRight className="w-4 h-4 mr-2 text-gray-400" />
                          {component}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            {/* Risques */}
            <AnalysisPoint title="Gestion des Risques" status="warning">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {implementation.risques.map((risk, idx) => (
                  <div key={idx} className="bg-white p-3 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">{risk.risque}</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        risk.impact === 'Élevé' 
                          ? 'bg-red-100 text-red-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        Impact: {risk.impact}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Mitigation: {risk.mitigation}
                    </p>
                  </div>
                ))}
              </div>
            </AnalysisPoint>

            {/* Gouvernance */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-4">Structure de Gouvernance</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-2">Comités</h4>
                  <ul className="space-y-1">
                    {implementation.governance.comités.map((comite, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <Users className="w-4 h-4 mr-2 text-blue-500" />
                        {comite}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="space-y-3">
                  <div>
                    <h4 className="text-sm font-medium">Fréquence de Suivi</h4>
                    <p className="text-sm text-gray-600">{implementation.governance.fréquence}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium">Reporting</h4>
                    <p className="text-sm text-gray-600">{implementation.governance.reporting}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default DigitalMaliAnalysis;